/* eslint-disable import/export -- Wildcard re-exports intentionally overlap with named re-exports to resolve conflicts between tamagui and lucide-react-native */

export type { UseMediaState } from '@tamagui/core'; // re-export whatever specific things we need from @tamagui/core
export { PortalProvider as TamaguiPortalProvider } from '@tamagui/portal';
export * from '@tamagui/toast'; // re-export all of @tamagui/toast
export * from 'lucide-react-native';
export * from 'tamagui'; // re-export all of tamagui
export {
    // explicit re-export of these which have conflicts with 'lucide-react-native/dist/lucide-react-native.suffixed'
    Anchor,
    type Box,
    Circle,
    Form,
    Frame,
    Group,
    Heading,
    Image,
    Section,
    Square,
    Text,
    type Variable,
    View,
} from 'tamagui';
export { Input as TamaguiInput } from 'tamagui';

// export custom and customized components
export { default as ArrowSplit } from './assets/arrow-split.svg';
export { default as ArrowStraight } from './assets/arrow-straight.svg';
export { default as Dots } from './assets/dots.svg';
export { default as FruitfulLogo } from './assets/fruitful-logo-icon-only.svg';
export * from './src/components/Animations';
export * from './src/components/avatars/MemberAvatar';
export {
    BadgeAction,
    type BadgeActionVariant,
    BadgeLabel,
    type BadgeLabelVariant,
} from './src/components/badges/Badge';
export {
    BackButton,
    BackButtonIconType,
} from './src/components/buttons/BackButton';
export {
    Button,
    ButtonSize,
    ButtonVariant,
    IconButton,
    InfoButton,
    PillButton,
    TabButton,
} from './src/components/buttons/Button';
export { CloseButton } from './src/components/buttons/CloseButton';
export { TileButton } from './src/components/buttons/TileButton';
export { AccountAllocationCard } from './src/components/cards/AccountAllocationCard';
export {
    GoalSetSection,
    type GoalSetSectionItem,
    type GoalSetSectionProps,
} from './src/components/cards/GoalSetSection';
export * from './src/components/componentHelpers';
export * from './src/components/containers/Containers';
export * from './src/components/containers/ScreenContainer';
export { ControlledCheckbox } from './src/components/controlled/ControlledCheckbox';
export * from './src/components/controlled/ControlledDateInput';
export * from './src/components/controlled/ControlledDateTextInput';
export * from './src/components/controlled/ControlledDollarInput';
export * from './src/components/controlled/ControlledInput';
export * from './src/components/controlled/ControlledPercentInput';
export * from './src/components/controlled/ControlledPhoneNumberInput';
export * from './src/components/controlled/ControlledPicker';
export * from './src/components/controlled/ControlledSSNInput';
export * from './src/components/date/DatePicker';
export * from './src/components/date/DateRangeSelectionSheet';
export * from './src/components/date/DateSelectionSheet';
export * from './src/components/date/LastUpdated';
export { FAQAccordionScreen } from './src/components/faq/FAQAccordionScreen';
export {
    FrostedSurface,
    type FrostedSurfaceProps,
} from './src/components/FrostedSurface';
export * from './src/components/guide/GuideBlurb';
export { BillCategoryIcons } from './src/components/icons/BillIcons';
export { default as CalendarDollarSignIcon } from './src/components/icons/custom/CalendarDollarSign.svg';
export { default as CircleNumberFiveIcon } from './src/components/icons/custom/CircleNumberFive.svg';
export { default as CircleNumberFourIcon } from './src/components/icons/custom/CircleNumberFour.svg';
export { default as CircleNumberOneIcon } from './src/components/icons/custom/CircleNumberOne.svg';
export { default as CircleNumberSixIcon } from './src/components/icons/custom/CircleNumberSix.svg';
export { default as CircleNumberThreeIcon } from './src/components/icons/custom/CircleNumberThree.svg';
export { default as CircleNumberTwoIcon } from './src/components/icons/custom/CircleNumberTwo.svg';
export { default as DraggerIcon } from './src/components/icons/custom/Dragger.svg';
export { default as FruitfulAccountIcon } from './src/components/icons/custom/FruitfulAccount.svg';
export { default as FruitfulLogoIcon } from './src/components/icons/custom/FruitfulLogo.svg';
export { default as MoneyMapIcon } from './src/components/icons/custom/MoneyMap.svg';
export { default as MoneySystemIcon } from './src/components/icons/custom/MoneySystem.svg';
export { FruitfulCustomIcon } from './src/components/icons/FruitfulCustomIcon';
export {
    DEFAULT_COLOR,
    DEFAULT_ICON,
    FRUITFUL_ICON_KEY,
    SPACE_COLOR_KEYS,
    SPACE_COLORS,
    SPACE_ICONS,
    SPACE_SECONDARY_COLORS,
    SPACE_TERTIARY_COLORS,
} from './src/components/icons/FruitfulCustomIconDefaults';
export { Icon, type IconType } from './src/components/icons/Icon';
export {
    Checkbox,
    CheckboxRowList,
    CheckboxWithLabel,
    LegacyCheckboxRow,
} from './src/components/inputs/Checkbox';
export {
    CircularSelectionIndicator,
    type CircularSelectionIndicatorProps,
} from './src/components/inputs/CircularSelectionIndicator';
export {
    DateInput,
    type DateInputProps,
} from './src/components/inputs/DateInput';
export {
    DollarInput,
    type DollarInputProps,
} from './src/components/inputs/DollarInput';
export { Input, type InputProps } from './src/components/inputs/Input';
export * from './src/components/inputs/InputTrigger';
export { KeyboardCompatibleBottomSheetTextInput } from './src/components/inputs/KeyboardCompatibleBottomSheetTextInput';
export { MultilineInput } from './src/components/inputs/MultilineInput';
export {
    PercentInput,
    type PercentInputProps,
} from './src/components/inputs/PercentInput';
export {
    PhoneNumberInput,
    type PhoneNumberInputProps,
} from './src/components/inputs/PhoneNumberInput';
export * from './src/components/inputs/Picker';
export { PrivateInput } from './src/components/inputs/PrivateInput';
export * from './src/components/inputs/RadioInput';
export { Slider } from './src/components/inputs/Slider';
export { SlideRulePicker } from './src/components/inputs/SlideRulePicker';
export { SSNInput, type SSNInputProps } from './src/components/inputs/SSNInput';
export {
    StyledBodyMediumEmphasizedInput,
    StyledBodyMediumRegularInput,
    StyledBodyMediumRegularInputNoOutline,
    StyledH1EmphasizedInput,
    StyledH1EmphasizedInputNoOutline,
    StyledH2Input,
    StyledH5Input,
    type StyledInput,
} from './src/components/inputs/StyledInputs';
export { default as Switch } from './src/components/inputs/Switch';
export * from './src/components/loading/Skeleton';
export { HolisticAccountLogo } from './src/components/logos/HolisticAccountLogo';
export { InstitutionLogo } from './src/components/logos/InstitutionLogo';
export { SvgUri } from './src/components/logos/Svg';
export * from './src/components/MemberTip/MemberTip';
export * from './src/components/modals/Modals';
export * from './src/components/navigation/ChecklistCompletionIndicator';
export * from './src/components/navigation/DotNavigation';
export * from './src/components/navigation/NavigationTile';
export * from './src/components/navigation/WorkflowStepIndicator';
export * from './src/components/notices/Notice';
export * from './src/components/rows/AccountRow';
export * from './src/components/rows/BadgeRow';
export * from './src/components/rows/BulletPointRow';
export * from './src/components/rows/CheckboxRow';
export * from './src/components/rows/InfoRow';
export * from './src/components/rows/NavigationRow';
export * from './src/components/rows/Row';
export * from './src/components/rows/RowHeader';
export * from './src/components/rows/RowList';
export * from './src/components/rows/SwitchRow';
export * from './src/components/rows/TextRow';
export { Private, redactPrivate } from './src/components/security/Private';
export { SegmentedControl } from './src/components/segmented-controls/SegmentedControl';
export {
    IconSelectDropdown,
    type IconSelectDropdownOption,
    type IconSelectDropdownProps,
} from './src/components/select/IconSelectDropdown';
export { SelectDropdown } from './src/components/select/SelectDropdown';
export { SelectMultiple } from './src/components/select/SelectMultiple';
export { SelectSingle } from './src/components/select/SelectSingle';
export * from './src/components/select/SelectTile';
export { Shadows, type ShadowStyle } from './src/components/Shadows';
export { KeyboardCompatibleBottomSheet } from './src/components/sheet/KeyboardCompatibleBottomSheet';
export * from './src/components/sheet/PdfSheet';
export {
    PremiumInfoSheet,
    type PremiumInfoSheetAction,
    type PremiumInfoSheetProps,
} from './src/components/sheet/PremiumInfoSheet';
export { Sheet } from './src/components/sheet/Sheet';
export { TallSheet } from './src/components/sheet/TallSheet';
export { SpinnerIcon } from './src/components/spinners/SpinnerIcon';
export {
    FullScreenSpinner,
    Spinner,
    SpinnerSize,
} from './src/components/spinners/Spinners';
export * from './src/components/text/SuperscriptText';
export * from './src/components/text/Typography';
export {
    ExpandingTile,
    ExpandingTilePreview,
    type ExpandingTilePreviewProps,
    type ExpandingTileProps,
} from './src/components/tiles';
export * from './src/components/tiles/BookNextSessionWidget';
export * from './src/components/tiles/NextAction';
export * from './src/components/tiles/UpcomingSessionWidget';
export { Toast } from './src/components/toast/Toast';
export {
    InfoButtonWithTooltip,
    TooltipGroupProvider,
    TooltipTrigger,
} from './src/components/tooltip/Tooltip';
export * from './src/hooks/useTooltipController';
export { smoothieTamaguiConfig, tokens } from './src/tamagui.config';
export {
    FontFamilyName,
    getDefaultFontFamily,
} from './src/themes/SmoothieFontTokens';
export { themes } from './src/themes/SmoothieThemes';
export { Button as TamaguiButton } from 'tamagui';
export { Tooltip as TamaguiTooltip } from 'tamagui'; // export helpful objects

// export helpful types
type TokenKey = number | string;
type TokenValue = number;
export type Tokens = Record<TokenKey, TokenValue>;

// export helpful constants & functions
export * from './src/constants';
export * from './src/misc';
export {
    colorPrimitiveTokens,
    type ColorTokenKey,
    useThemeColors,
} from './src/themes/SmoothieColorTokens';

// gallery
export { BlenderOverview } from './src/design-system-gallery/BlenderOverview';
export { BlenderView } from './src/design-system-gallery/BlenderView';
export { OpenDevDashboardButton } from './src/design-system-gallery/OpenDevDashboardButton';
